import type { Metadata } from 'next';

export interface MetadataProps {
  title?: string;
  description?: string;
  keywords?: string;
  author?: string;
  ogTitle?: string;
  ogDescription?: string;
  ogImage?: string;
  ogUrl?: string;
  twitterCard?: "summary_large_image" | "summary" | "player" | "app";
  twitterTitle?: string;
  twitterDescription?: string;
  twitterImage?: string;
  canonicalUrl?: string;
  /** Mark the page as noindex/nofollow (used for private, logged-in pages). */
  noindex?: boolean;
}

export function generateMetadata({
  title,
  description,
  keywords,
  author = 'LovingEcards',
  ogTitle,
  ogDescription,
  ogImage,
  ogUrl,
  twitterCard = 'summary_large_image',
  twitterTitle,
  twitterDescription,
  twitterImage,
  canonicalUrl,
  noindex = false,
}: MetadataProps): Metadata {
  // Default title and descriptions
  const defaultTitle = 'LovingEcards | Group Ecards & Digital Invitations';
  const defaultDescription =
    'Create free group ecards everyone can sign, plus digital invitations for birthdays, farewells, weddings and every occasion.';

  // Images are only set when explicitly passed. Otherwise Next.js falls back to the
  // `app/opengraph-image.tsx` file convention (or a route's own opengraph-image).
  // Construct the metadata object
  return {
    title: title || defaultTitle,
    description: description || defaultDescription,
    keywords: keywords,
    authors: author ? [{ name: author }] : undefined,
    openGraph: {
      title: ogTitle || title || defaultTitle,
      description: ogDescription || description || defaultDescription,
      images: ogImage ? [{ url: ogImage }] : undefined,
      url: ogUrl || canonicalUrl,
      type: 'website',
    },
    twitter: {
      card: twitterCard,
      title: twitterTitle || ogTitle || title || defaultTitle,
      description: twitterDescription || ogDescription || description || defaultDescription,
      images: twitterImage ? [twitterImage] : ogImage ? [ogImage] : undefined,
    },
    alternates: {
      canonical: canonicalUrl,
    },
    robots: noindex
      ? { index: false, follow: false, googleBot: { index: false, follow: false } }
      : undefined,
  };
}

// Helper function to generate JSON-LD schema markup
export function generateSchemaMarkup(schema: object): string {
  return JSON.stringify(schema);
}
